/**
 * useHomeARMasterListViewLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeARMasterListView
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge, __subscribeListens } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeARDemoStockItem, HomeARMasterListViewEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';

// ============================================================================
// State Machine
// ============================================================================

export type HomeARMasterListViewState =
  | 'idle';

export type HomeARMasterListViewEvent =
  | 'INIT'
  | 'DataListLoaded'
  | 'REORDER'
  | 'VIEW'
  | 'SWIPE_LEFT'
  | 'SWIPE_RIGHT'
  | 'LONG_PRESS'
  | 'LOAD_MORE'
  | 'BrowseItemLoaded';

type HomeARMasterListViewSlot = 'main';
interface HomeARMasterListViewInternalState {
  machineState: HomeARMasterListViewState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARMasterListViewEvent | null;
  slotWriter: Partial<Record<HomeARMasterListViewSlot, HomeARMasterListViewEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARMasterListViewEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoStockItem>;
}

type HomeARMasterListViewAction =
  | { type: 'EVENT_SUCCESS'; event: HomeARMasterListViewEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeARMasterListViewSlot[]; renderClear?: HomeARMasterListViewSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeARMasterListViewEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeARDemoStockItem & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeARMasterListViewReducer(
  state: HomeARMasterListViewInternalState,
  action: HomeARMasterListViewAction
): HomeARMasterListViewInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeARMasterListViewState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeARDemoStockItem>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeARDemoStockItem>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeARDemoStockItem>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeARMasterListViewInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { SExpr } from '@almadar/core';

export interface HomeARMasterListViewConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  accepts?: string;
  ariaBusy?: boolean;
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  ariaDescribedby?: string;
  ariaExpanded?: boolean;
  ariaHidden?: boolean;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaLive?: 'off' | 'polite' | 'assertive';
  ariaPressed?: 'true' | 'false' | 'mixed';
  ariaSelected?: boolean;
  className?: string;
  columns: { colorMap?: Record<string, string>; format?: 'date' | 'time' | 'datetime' | 'currency' | 'number' | 'boolean' | 'percent'; icon?: string; label?: string; labels?: Record<string, string>; name: string; variant?: 'h3' | 'h4' | 'overline' | 'avatar' | 'body' | 'caption' | 'badge' | 'small' | 'progress' }[];
  currentUser?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
  dndItemIdField?: string;
  dndRoot?: boolean;
  dragGroup?: string;
  dropEvent?: string;
  emptyAction?: { event?: string; label: string };
  emptyDescription?: string;
  emptyIcon?: string;
  emptyMessage?: string;
  emptyTitle?: string;
  enter?: 'none' | 'fade' | 'rise' | 'scale' | 'slide';
  enterDelay?: number;
  error?: { code?: string; message: string; name?: string; stack?: string };
  fields: { label: string; name: string; variant: string }[];
  gap: 'none' | 'sm' | 'md' | 'lg';
  groupBy?: string;
  groupFormat?: 'date' | 'time' | 'datetime' | 'currency' | 'number' | 'boolean' | 'percent';
  hasMore?: boolean;
  infiniteScroll?: boolean;
  isLoading: boolean;
  itemActions: { event: string; icon?: string; label: string; navigatesTo?: string; payload?: SExpr; roles?: string[]; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; when?: SExpr }[];
  itemClickEvent: string;
  itemEnter?: 'none' | 'fade' | 'rise' | 'scale' | 'slide';
  lang?: string;
  loadMoreEvent: string;
  longPressEvent: string;
  look: 'dense' | 'spacious' | 'striped' | 'borderless' | 'card-rows';
  maxInlineActions: number;
  pageSize: number;
  positionEvent?: string;
  relationsData: {  };
  reorderEvent: string;
  reorderable?: boolean;
  role?: 'alert' | 'alertdialog' | 'application' | 'article' | 'banner' | 'blockquote' | 'button' | 'caption' | 'cell' | 'checkbox' | 'code' | 'columnheader' | 'combobox' | 'complementary' | 'contentinfo' | 'definition' | 'deletion' | 'dialog' | 'document' | 'emphasis' | 'feed' | 'figure' | 'form' | 'generic' | 'grid' | 'gridcell' | 'group' | 'heading' | 'img' | 'insertion' | 'link' | 'list' | 'listbox' | 'listitem' | 'log' | 'main' | 'marquee' | 'math' | 'menu' | 'menubar' | 'menuitem' | 'menuitemcheckbox' | 'menuitemradio' | 'meter' | 'navigation' | 'none' | 'note' | 'option' | 'paragraph' | 'presentation' | 'progressbar' | 'radio' | 'radiogroup' | 'region' | 'row' | 'rowgroup' | 'rowheader' | 'scrollbar' | 'search' | 'searchbox' | 'separator' | 'slider' | 'spinbutton' | 'status' | 'strong' | 'subscript' | 'superscript' | 'switch' | 'tab' | 'table' | 'tablist' | 'tabpanel' | 'term' | 'textbox' | 'time' | 'timer' | 'toolbar' | 'tooltip' | 'tree' | 'treegrid' | 'treeitem';
  selfFetch: boolean;
  senderField?: string;
  senderLabelField?: string;
  skeleton: 'header' | 'table' | 'list' | 'grid' | 'detail' | 'stats' | 'form' | 'card' | 'text' | 'none';
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  sortable?: boolean;
  surface: 'auto' | 'none';
  swipeLeftActions: { icon?: string; label: string; variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }[];
  swipeLeftEvent: string;
  swipeRightActions: { icon?: string; label: string; variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }[];
  swipeRightEvent: string;
  tabIndex?: number;
  variant: 'default' | 'card' | 'compact' | 'message';
  viewerRole: string;
}

const DEFAULT_HOME_AR_MASTER_LIST_VIEW_CONFIG = {
  columns: [],
  fields: [{ label: "Name", name: "name", variant: "h4" }, { label: "Description", name: "description", variant: "caption" }, { label: "Status", name: "status", variant: "badge" }],
  gap: "none",
  isLoading: false,
  itemActions: [],
  itemClickEvent: "VIEW",
  loadMoreEvent: "LOAD_MORE",
  longPressEvent: "LONG_PRESS",
  look: "dense",
  maxInlineActions: 0,
  pageSize: 5,
  relationsData: {  },
  reorderEvent: "REORDER",
  selfFetch: false,
  skeleton: "list",
  surface: "none",
  swipeLeftActions: [],
  swipeLeftEvent: "SWIPE_LEFT",
  swipeRightActions: [],
  swipeRightEvent: "SWIPE_RIGHT",
  variant: "compact",
  viewerRole: "",
} as HomeARMasterListViewConfig;

export interface HomeARMasterListViewLogicReturn {
  state: HomeARMasterListViewState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARMasterListViewEvent | null;
  slotWriter: Partial<Record<HomeARMasterListViewSlot, HomeARMasterListViewEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARMasterListViewEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoStockItem>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeARMasterListViewEventPayloadMap]: (payload?: HomeARMasterListViewEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeARMasterListViewConfig;
}

const __CASCADE_EVENTS = new Set<string>(['BrowseItemLoaded', 'DataListLoaded']);

export function useHomeARMasterListViewLogic(config?: Partial<HomeARMasterListViewConfig>): HomeARMasterListViewLogicReturn {
  const mergedConfig: HomeARMasterListViewConfig = { ...DEFAULT_HOME_AR_MASTER_LIST_VIEW_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-ar-master-list-view', orbitalName: 'HomeAR', traitName: 'HomeARMasterListView' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeARMasterListViewReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeARDemoStockItem'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT' && (false)) {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'INIT' && (!false)) {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'DataListLoaded') {
      // idle → idle (on DataListLoaded)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeARMasterListViewSlot[]; clear: HomeARMasterListViewSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'DataListLoaded': return { write: ['main'] as HomeARMasterListViewSlot[], clear: [] as HomeARMasterListViewSlot[] };
          case 'INIT': return { write: ['main'] as HomeARMasterListViewSlot[], clear: [] as HomeARMasterListViewSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeARDemoStockItem': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeARMasterListViewEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeARMasterListView': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeARMasterListViewEventPayloadMap]: (payload?: HomeARMasterListViewEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'DataListLoaded': (payload?: HomeARMasterListViewEventPayloadMap['DataListLoaded']) => dispatch('DataListLoaded', payload),
    'REORDER': () => dispatch('REORDER'),
    'VIEW': (payload?: HomeARMasterListViewEventPayloadMap['VIEW']) => dispatch('VIEW', payload),
    'SWIPE_LEFT': () => dispatch('SWIPE_LEFT'),
    'SWIPE_RIGHT': () => dispatch('SWIPE_RIGHT'),
    'LONG_PRESS': () => dispatch('LONG_PRESS'),
    'LOAD_MORE': () => dispatch('LOAD_MORE'),
  };

  useUIEvents(enqueueEvent, 'HomeAR.HomeARMasterListView', ['INIT', 'DataListLoaded'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeARMasterListView', (): TraitStateSnapshot => ({
      traitName: 'HomeARMasterListView',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'DataListLoaded', 'REORDER', 'VIEW', 'SWIPE_LEFT', 'SWIPE_RIGHT', 'LONG_PRESS', 'LOAD_MORE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'BrowseItemLoaded' (from HomeARDemoStockPageList) → trigger 'DataListLoaded'
    const unsub_browseitemloaded_from_homeardemostockpagelist_to_datalistloaded = __subscribeListens(eventBus, 'UI:HomeAR.HomeARDemoStockPageList.BrowseItemLoaded', 'HomeARMasterListView', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeAR.HomeARMasterListView', 'DataListLoaded')) return;
      enqueueEvent('DataListLoaded', event.payload);
    });
    return () => {
      if (typeof unsub_browseitemloaded_from_homeardemostockpagelist_to_datalistloaded === 'function') unsub_browseitemloaded_from_homeardemostockpagelist_to_datalistloaded();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
